import React, { useEffect, useRef } from 'react';
import { Box, HiddenFromView, Text, useAtBreakpoint } from '@stitch-fix/mode-react';
import classNames from 'classnames';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { recordCustomTiming, recordResources } from '@stitch-fix/client-analytics-reporter';
import { CloudinaryVideoPlayer, type CloudinaryVideoPlayerProps } from '../../CloudinaryVideoPlayer';
import { GetStartedButton, type GetStartedButtonProps } from '../../GetStartedButton';
import { buttonColorToButtonVariant } from '../../ModuleFooter/ModuleFooter';
import { useUrlFromVideoBreakpoints, videoBreakpointDefault, type VideoBreakpointProps } from '../utils';
import { useStickyCtaContext } from '../../StickyCta/useStickyCtaContext';
import { getFileName } from '../../CloudinaryVideoPlayer/utils';
import styles from './styles.module.scss';
export type VideoFullBleedHeroProps = {
  titleText?: string;
  titleShadow?: boolean;
  titleHiddenFromView?: boolean;
  contentOrder?: 'Title/CTA first' | 'Video first' | null;
  ctaText?: GetStartedButtonProps['copy'];
  ctaHref?: GetStartedButtonProps['href'];
  ctaColor?: string;
  ctaHiddenFromView?: boolean;
} & VideoBreakpointProps;
const buttonColorToPlayPauseVariant = (buttonColor?: string): CloudinaryVideoPlayerProps['playPauseButtonVariant'] => {
  switch (buttonColor) {
    case 'mint':
      return 'promo-dark';
    case 'navy':
      return 'navy-white';
    case 'holiday forest':
      return 'forest';
    case 'holiday grapefruit':
      return 'grapefruit';
    case 'holiday snow':
      return 'info-light';
    case 'citrus':
      return 'citrus';
    default:
      return 'navy-white';
  }
};
export const VideoFullBleedHero = ({
  titleText,
  titleShadow,
  titleHiddenFromView,
  contentOrder,
  ctaText,
  ctaHref,
  ctaHiddenFromView,
  ctaColor = 'navy',
  videoDescription,
  mobileVideo,
  mobileVideoAspectRatio = 'auto',
  tabletVideo,
  tabletVideoAspectRatio = 'auto',
  desktopVideo,
  desktopVideoAspectRatio = 'auto',
  soundControls,
  captions,
  playPauseButtonSize,
  playPauseButtonAlignment
}: VideoFullBleedHeroProps) => {
  const resourcesRecorded = useRef(false);
  const autoPlayTriggered = useRef(false);
  const {
    reportSelectEvent
  } = useSelectEvent();
  const {
    setStickyContainerTarget
  } = useStickyCtaContext();
  const videoUrl = useUrlFromVideoBreakpoints({
    url: {
      mobile: mobileVideo,
      tablet: tabletVideo,
      desktop: desktopVideo
    }
  });
  useEffect(() => {
    if (resourcesRecorded.current) {
      return;
    }
    resourcesRecorded.current = true;
    recordResources('video_full_bleed_hero_loaded', [getFileName(videoUrl)]);
  }, [videoUrl]);
  const isDesktop = useAtBreakpoint(videoBreakpointDefault.desktop);
  const isTablet = useAtBreakpoint(videoBreakpointDefault.tablet);
  const videoAboveCTA = contentOrder === 'Video first';
  const shouldMakeVideoClickable = ctaHiddenFromView && ctaHref;
  const ctaLabel = ctaText || 'Get started';
  let aspectRatio = mobileVideoAspectRatio;
  if (isDesktop) {
    aspectRatio = desktopVideoAspectRatio;
  } else if (isTablet) {
    aspectRatio = tabletVideoAspectRatio;
  }
  const handleOnAutoPlay = () => {
    if (autoPlayTriggered.current) {
      return;
    }
    autoPlayTriggered.current = true;
    recordCustomTiming('video_full_bleed_hero_auto_play_triggered');
  };
  const handleButtonClick = () => {
    if (ctaHref === '/signup') {
      reportSelectEvent({
        action_name: 'navigate_to_signup',
        content_id: 'hero_video',
        name: 'take_style_quiz_button',
        schema: 'select'
      });
    }
  };
  return <Box className={classNames(styles.container, {
    [styles.videoFirst]: videoAboveCTA
  })} data-testid="video-full-bleed-container" ref={setStickyContainerTarget}>
      <Box className={styles.textContainer}>
        {titleText && titleHiddenFromView && <HiddenFromView>
            <h1 data-testid="hidden-from-view">{titleText}</h1>
          </HiddenFromView>}
        {titleText && !titleHiddenFromView && <Text as="h1" setting="title-xxlarge" className={classNames({
        [styles.dropShadow]: titleShadow
      })} spacingBottom>
            {titleText}
          </Text>}
        {(ctaText || ctaHref) && !ctaHiddenFromView && <GetStartedButton copy={ctaText} href={ctaHref} variant={buttonColorToButtonVariant(ctaColor)} eventLocation="hero" />}
      </Box>
      <Box className={classNames(styles.videoContainer, {
      [styles.clickable]: shouldMakeVideoClickable
    })} {...shouldMakeVideoClickable && {
      as: 'a',
      href: ctaHref,
      'aria-label': ctaLabel,
      onClick: handleButtonClick
    }}>
        <CloudinaryVideoPlayer key={aspectRatio} videoDescription={videoDescription} videoUrl={videoUrl} soundControls={soundControls} captions={captions} playPauseButtonSize={playPauseButtonSize} playPauseButtonAlignment={playPauseButtonAlignment} playPauseButtonVariant={buttonColorToPlayPauseVariant(ctaColor)} aspectRatio={aspectRatio} onAutoPlay={handleOnAutoPlay} />
      </Box>
    </Box>;
};